/**
 * InlineCitation — [1][2] 소스 인용 마커
 *
 * AI 메시지 텍스트 내 [n] 패턴을 찾아 클릭 가능한 버튼으로 변환한다.
 * 클릭 시 onDeepLink(citation.route) 호출
 */

import { cn } from "@/shared/lib/utils";
import type { Citation } from "@modootoday/core-agent/types";

export interface InlineCitationProps {
  /** [n] 마커로 표시할 인용 번호 (1-indexed) */
  index: number;
  citation: Citation;
  onDeepLink?: (path: string) => void;
  className?: string;
}

export function InlineCitation({
  index,
  citation,
  onDeepLink,
  className,
}: InlineCitationProps) {
  function handleClick() {
    onDeepLink?.(citation.route);
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      title={citation.label}
      className={cn(
        "inline-flex size-4 items-center justify-center rounded",
        "bg-accent/10 text-text-brand text-[9px] font-bold",
        "hover:bg-accent/20 transition-colors cursor-pointer",
        "align-super leading-none",
        className,
      )}
      aria-label={`출처 ${index}: ${citation.label}`}
    >
      {index}
    </button>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   CitationList — 메시지 하단 인용 목록
   ══════════════════════════════════════════════════════════════════════════ */

export interface CitationListProps {
  citations: Citation[];
  onDeepLink?: (path: string) => void;
  className?: string;
}

export function CitationList({
  citations,
  onDeepLink,
  className,
}: CitationListProps) {
  if (citations.length === 0) return null;

  return (
    <div className={cn("mt-3 space-y-1.5", className)}>
      <p className="text-[10px] font-medium uppercase tracking-wide text-text-tertiary">
        출처
      </p>
      {citations.map((citation, i) => (
        <button
          key={citation.id}
          type="button"
          onClick={() => onDeepLink?.(citation.route)}
          className="flex items-start gap-2 text-left hover:opacity-80 transition-opacity"
        >
          <span className="mt-0.5 flex size-3.5 shrink-0 items-center justify-center rounded bg-accent/10 text-[9px] font-bold text-text-brand">
            {i + 1}
          </span>
          <span className="text-[11px] text-text-tertiary hover:text-text-brand transition-colors">
            {citation.label}
            {citation.recordCount !== undefined && (
              <span className="ml-1 text-[10px]">
                ({citation.recordCount.toLocaleString()}건)
              </span>
            )}
          </span>
        </button>
      ))}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   parseCitationText — 텍스트에서 [n] 마커를 JSX로 치환
   ══════════════════════════════════════════════════════════════════════════ */

export function parseCitationText(
  text: string,
  citations: Citation[],
  onDeepLink?: (path: string) => void,
): React.ReactNode {
  if (citations.length === 0) return text;

  // [1], [2] ... 패턴 분할
  const parts = text.split(/(\[\d+\])/g);

  return parts.map((part, idx) => {
    const match = part.match(/^\[(\d+)\]$/);
    if (!match) return part;

    const citationIndex = parseInt(match[1]!, 10);
    const citation = citations[citationIndex - 1];
    if (!citation) return part;

    return (
      <InlineCitation
        key={`${idx}-${citationIndex}`}
        index={citationIndex}
        citation={citation}
        onDeepLink={onDeepLink}
      />
    );
  });
}
